import React from 'react';
import DocumentList from '../documentList/documentList.jsx';
import Gallery from '../gallery/gallery.jsx';
import GridTabs from '../list/grid/gridTabs.jsx';
import './MediaFiles.style.scss';

class MediaFiles extends React.Component {
  heightRecalcDelays = [80, 180, 320];

  constructor(props) {
    super(props);
    this.tabContentInnerRef = React.createRef();
    this.tabAnimationTimer = null;
    this.heightRecalcTimers = [];
    this.resizeRafId = null;
    this.resizeObserverRafId = null;
    this.tabContentResizeObserver = null;
    this.lastObservedTabNode = null;
    this.state = this.getInitialStateFromProps(props);
  }

  componentDidMount() {
    this.observeTabContentSize();
    window.addEventListener('resize', this.handleWindowResize);
  }

  componentDidUpdate(prevProps) {
    this.observeTabContentSize();

    if (prevProps.data.files !== this.props.data.files || prevProps.data.state !== this.props.data.state) {
      const newState = this.getInitialStateFromProps(this.props);
      this.setState({
        actionCloseGallery: newState.actionCloseGallery,
        onCreateAppointment: newState.onCreateAppointment,
        media: newState.media,
        documents: newState.documents,
        serviceState: newState.serviceState,
        // Preserve the selectedTab state
        //selectedTab: this.state.selectedTab
        selectedTab: newState.selectedTab

      });

      if (this.state.animatedHeight == null) {
        this.schedulePanelHeightRecalculation();
      }
    }
  }

  componentWillUnmount() {
    this.clearTabAnimationTimer();

    if (this.resizeRafId != null) {
      window.cancelAnimationFrame(this.resizeRafId);
      this.resizeRafId = null;
    }

    if (this.resizeObserverRafId != null) {
      window.cancelAnimationFrame(this.resizeObserverRafId);
      this.resizeObserverRafId = null;
    }

    window.removeEventListener('resize', this.handleWindowResize);

    this.clearHeightRecalcTimers();

    if (this.tabContentResizeObserver) {
      this.tabContentResizeObserver.disconnect();
      this.tabContentResizeObserver = null;
    }

    this.lastObservedTabNode = null;
  }

  clearTabAnimationTimer = () => {
    if (!this.tabAnimationTimer) {
      return;
    }

    clearTimeout(this.tabAnimationTimer);
    this.tabAnimationTimer = null;
  }

  clearHeightRecalcTimers = () => {
    if (!this.heightRecalcTimers.length) {
      return;
    }

    this.heightRecalcTimers.forEach((timerId) => clearTimeout(timerId));
    this.heightRecalcTimers = [];
  }

  handleWindowResize = () => {
    if (this.resizeRafId != null) {
      return;
    }

    this.resizeRafId = window.requestAnimationFrame(() => {
      this.resizeRafId = null;
      this.schedulePanelHeightRecalculation();
    });
  }

  getMeasuredTabContentHeight = () => {
    const tabNode = this.tabContentInnerRef.current;
    if (!tabNode) {
      return null;
    }

    const rectHeight = tabNode.getBoundingClientRect ? tabNode.getBoundingClientRect().height : 0;
    const measuredHeight = Math.max(tabNode.scrollHeight || 0, tabNode.offsetHeight || 0, rectHeight || 0);
    return measuredHeight ? Math.ceil(measuredHeight) : null;
  }

  applyMeasuredPanelHeight = () => {
    const measuredHeight = this.getMeasuredTabContentHeight();
    if (measuredHeight == null) {
      return;
    }

    this.setState((prevState) => {
      if (prevState.animatedHeight === measuredHeight && prevState.minPanelHeight === measuredHeight) {
        return null;
      }

      return {
        animatedHeight: measuredHeight,
        minPanelHeight: measuredHeight,
      };
    });
  }

  schedulePanelHeightRecalculation = () => {
    this.clearHeightRecalcTimers();

    window.requestAnimationFrame(() => {
      this.applyMeasuredPanelHeight();

      this.heightRecalcDelays.forEach((delay) => {
        const timerId = window.setTimeout(() => {
          this.applyMeasuredPanelHeight();
        }, delay);
        this.heightRecalcTimers.push(timerId);
      });
    });
  }

  observeTabContentSize = () => {
    const tabNode = this.tabContentInnerRef.current;
    if (!tabNode || this.lastObservedTabNode === tabNode || typeof ResizeObserver === 'undefined') {
      return;
    }

    if (!this.tabContentResizeObserver) {
      this.tabContentResizeObserver = new ResizeObserver(() => {
        if (this.resizeObserverRafId != null) {
          return;
        }

        this.resizeObserverRafId = window.requestAnimationFrame(() => {
          this.resizeObserverRafId = null;
          this.applyMeasuredPanelHeight();
        });
      });
    }

    if (this.lastObservedTabNode) {
      this.tabContentResizeObserver.unobserve(this.lastObservedTabNode);
    }

    this.tabContentResizeObserver.observe(tabNode);
    this.lastObservedTabNode = tabNode;
  }

  getInitialStateFromProps = (props) => {
    const { files, state } = props.data;
    const { onCreateAppointment, actionCloseGallery } = props;

    const customerDocumentsLength = files?.customer?.documents?.length || 0;
    const providerDocumentsLength = files?.provider?.documents?.length || 0;
    const totalDocumentsLength = customerDocumentsLength + providerDocumentsLength;

    const customerMediaLength = files?.customer?.media?.length || 0;
    const providerMediaLength = files?.provider?.media?.length || 0;
    const totalMediaLength = customerMediaLength + providerMediaLength;

    let selectedTab = this.state?.selectedTab ?? 0;
    if (props.selectedTab != null) {
      selectedTab = props.selectedTab;
    }
    if (totalMediaLength===0 && totalDocumentsLength!==0) selectedTab=1;
    if (totalMediaLength!==0 && totalDocumentsLength===0) selectedTab=0;

    return {
      actionCloseGallery,
      onCreateAppointment,
      selectedTab,
      tabAnimationKey: 0,
      animatedHeight: null,
      minPanelHeight: null,
      media: {
        customer: files?.customer?.media,
        provider: files?.provider?.media
      },
      documents: {
        customer: files?.customer?.documents,
        provider: files?.provider?.documents
      },
      serviceState: state
    };
  };

  renderTabContent = (media, documents, selectedTab) => {
    const { serviceState, onCreateAppointment, actionCloseGallery} = this.state;
    if (selectedTab === 0) {
      return <Gallery data={{media, serviceState}} newMediaFileLoaded={this.props.newMediaFileLoaded}
        onCreateAppointment={onCreateAppointment}
        onDeleteMediaFile={this.props.onDeleteMediaFile}
        actionCloseGallery={actionCloseGallery}/>
    } else {
      return <DocumentList data={{documents, serviceState}}
        onCreateAppointment={onCreateAppointment}
        newMediaFileLoaded={this.props.newMediaFileLoaded} onDeleteMediaFile={this.props.onDeleteMediaFile}/>
    }
  }

  selectTab = (tab) => {
    if (tab === this.state.selectedTab) {
      return;
    }

    const currentHeight = this.tabContentInnerRef.current ? this.tabContentInnerRef.current.offsetHeight : null;
    if (this.props.onSelectedTabChange) {
      this.props.onSelectedTabChange(tab);
    }

    this.clearTabAnimationTimer();

    this.setState((prevState) => ({
      selectedTab: tab,
      tabAnimationKey: prevState.tabAnimationKey + 1,
      animatedHeight: currentHeight,
      minPanelHeight: currentHeight || prevState.minPanelHeight,
    }), () => {
      this.schedulePanelHeightRecalculation();

      this.tabAnimationTimer = window.setTimeout(() => {
        this.setState({ animatedHeight: null });
        this.tabAnimationTimer = null;
      }, 240);
    });
  }

  render() {
    const { media, documents, selectedTab, tabAnimationKey, animatedHeight, minPanelHeight } = this.state;
    const { styleClass } = this.props;
    const panelStyle = {
      minHeight: minPanelHeight != null ? `${minPanelHeight}px` : undefined,
      height: animatedHeight != null ? `${animatedHeight}px` : undefined,
    };

    return (
      <div className={`w-100 mb-0 pt-0 pb-3 ${styleClass ? styleClass : ''}`} >
        <GridTabs selectedTab={selectedTab} data={{ media, documents }} callback={this.selectTab}/>
        <div className={`media-tab-content-panel ${selectedTab === 0 ? 'media-tab-content-panel--left' : 'media-tab-content-panel--right'}`} style={panelStyle}>
          <div ref={this.tabContentInnerRef} key={`media-tab-content-${selectedTab}-${tabAnimationKey}`} className="media-tab-content-panel__inner">
            {this.renderTabContent(media, documents, selectedTab)}
          </div>
        </div>
      </div>
    );
  }
}

export default MediaFiles;